import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { t, font } from "@realtyflows/ui";
import { switchPortal, saveToken } from "../api";

/**
 * PortalSwitcher — V2 ADR-003 Phase 3, updated for Phase 4.4 wave 2.
 *
 * Reads `user.memberships` (populated by the API since Phase 2 — present
 * on /me, /login, /google). Renders:
 *   - 0 memberships → nothing (deactivated user)
 *   - 1 membership  → static chip showing the portal name
 *   - 2+ memberships → dropdown with all portals
 *
 * Phase 4.4 wave 2 change — switching no longer calls switchPortal()
 * inline + reloads. Instead it navigates to `/p/<slug>/<current-inner>`
 * which routes through the <PortalDeepLink /> entry. The deep-link
 * route does the JWT switch + redirect.
 *
 * Net effect:
 *   - The URL bar always reflects the active portal once you've
 *     switched, so the address is shareable + bookmarkable.
 *   - One code path for "user clicks the switcher" and "user pasted a
 *     /p/ link from outside the app." Less surface to keep in sync.
 *   - Teams without a slug fall back to the legacy inline switch path
 *     (graceful: works today even before every team has a slug set).
 *
 * Personal portal is always shown as "Personal" with the RealtyFlows mark.
 * Team portals show their logo + name, with first-letter fallback.
 */
export default function PortalSwitcher({ user, activeTeamId, onSwitched }) {
  const location = useLocation();
  const memberships = user?.memberships || [];
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState("");
  const wrapRef = useRef(null);

  // Click-outside close
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (memberships.length === 0) return null;

  // Coerce active id — server returns null for personal, but JWT-state may
  // surface as undefined or "null" string after a refresh; normalize.
  const active = memberships.find((m) =>
    (m.team_id == null && (activeTeamId == null || activeTeamId === "null"))
    || (m.team_id != null && Number(m.team_id) === Number(activeTeamId))
  ) || memberships[0];

  const label = (m) => m.team_id == null ? "Personal" : (m.team_name || `Team #${m.team_id}`);

  const onPick = async (m) => {
    if ((m.team_id || null) === (active.team_id || null)) { setOpen(false); return; }

    // Phase 4.4 wave 2: navigate by URL when we have a slug to use.
    // We don't use react-router's navigate() here because the rest of
    // the app reads from localStorage / JWT state that's set by the
    // <PortalDeepLink> route handler — a hard navigation (full doc
    // load) is the simplest way to make sure every component re-reads.
    const slug = m.team_id == null
      ? "personal"
      : (m.team_slug && String(m.team_slug).trim()) || null;
    if (slug) {
      setOpen(false);
      // Preserve the current inner path so the user lands on the same
      // page in the new portal (e.g. switching while looking at /clients
      // keeps them on /clients in the new portal).
      const innerPath = location.pathname + location.search;
      window.location.assign(`/p/${slug}${innerPath}`);
      return;
    }

    // Fallback for teams without a slug set: legacy inline switch path.
    // Once every team has a slug we can drop this branch.
    setSwitching(true);
    setError("");
    try {
      const result = await switchPortal(m.team_id);
      if (result?.token) saveToken(result.token);
      setOpen(false);
      if (onSwitched) onSwitched(result);
      else window.location.reload();
    } catch (e) {
      setError(e.message || "Switch failed");
    } finally {
      setSwitching(false);
    }
  };

  // Single membership → static chip (no dropdown noise).
  if (memberships.length === 1) {
    return (
      <PortalChip
        membership={active}
        label={label(active)}
        title="You belong to only one portal"
      />
    );
  }

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={switching}
        style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          background: t.surface, border: `1px solid ${t.border}`,
          borderRadius: 99, padding: "5px 14px 5px 5px",
          cursor: switching ? "wait" : "pointer", fontFamily: font.body,
          color: t.textPrimary, fontSize: 13, fontWeight: 600,
        }}
        title="Switch portal"
      >
        <PortalAvatar membership={active} />
        <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label(active)}</span>
        <svg width="10" height="10" viewBox="0 0 12 12" style={{ opacity: 0.5 }}>
          <path d="M2 4 L6 8 L10 4" stroke={t.textMuted} strokeWidth="1.5" fill="none" />
        </svg>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", right: 0,
          background: t.surface, border: `1px solid ${t.border}`,
          borderRadius: 10, padding: 4, minWidth: 220,
          boxShadow: "0 8px 24px rgba(12,27,51,0.12)", zIndex: 100,
        }}>
          {memberships.map((m) => {
            const isActive = (m.team_id || null) === (active.team_id || null);
            return (
              <button
                key={m.team_id == null ? "personal" : m.team_id}
                onClick={() => onPick(m)}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "8px 10px", borderRadius: 8, border: "none",
                  background: isActive ? t.surfaceWarm : "transparent",
                  cursor: "pointer", fontFamily: font.body, fontSize: 13,
                  color: t.textPrimary, textAlign: "left",
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = t.pageBg; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
              >
                <PortalAvatar membership={m} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {label(m)}
                  </div>
                  <div style={{ fontSize: 11, color: t.textMuted, marginTop: 1 }}>
                    {m.role}{m.team_id == null ? " · your private workspace" : ""}
                  </div>
                </div>
                {isActive && (
                  <span style={{ fontSize: 10, color: t.emerald, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Active</span>
                )}
              </button>
            );
          })}

          {error && (
            <div style={{ padding: "8px 10px", color: t.coral, fontSize: 12, borderTop: `1px solid ${t.border}`, marginTop: 4 }}>
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Static chip for the single-membership case. */
function PortalChip({ membership, label, title }) {
  return (
    <div
      style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        background: t.surfaceWarm, border: `1px solid ${t.border}`,
        borderRadius: 99, padding: "5px 14px 5px 5px",
        fontFamily: font.body, color: t.textPrimary, fontSize: 13, fontWeight: 600,
      }}
      title={title}
    >
      <PortalAvatar membership={membership} />
      <span>{label}</span>
    </div>
  );
}

/**
 * Logo-or-initial avatar for a portal.
 * Personal portal shows the RealtyFlows mark color. Team uses team logo
 * if present, else the first letter of the team name in the team's
 * primary color (if set) or gold.
 */
function PortalAvatar({ membership }) {
  const isPersonal = membership.team_id == null;
  if (isPersonal) {
    return (
      <span style={avatarStyle({ bg: t.navy, color: "#fff" })}>R</span>
    );
  }
  if (membership.logo_url) {
    return (
      <img
        src={membership.logo_url}
        alt=""
        style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
      />
    );
  }
  const letter = (membership.team_name || "?").trim().charAt(0).toUpperCase();
  const bg = membership.primary_color || t.emerald;
  return <span style={avatarStyle({ bg, color: "#fff" })}>{letter}</span>;
}

function avatarStyle({ bg, color }) {
  return {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 24, height: 24, borderRadius: "50%",
    background: bg, color, fontSize: 12, fontWeight: 700,
    fontFamily: "ui-monospace, monospace", flexShrink: 0,
  };
}
